import React, { useState } from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import plainCloseIcon from '../../asset/images/icons/plain-close-icon.svg';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { RichTextViewer } from '../Common/RichTextViewer';

interface ReadMoreProps extends TypographyProps {
    text: string;
    header?: string;
    maxChars: number;
    textColor?: string;
}

const ReadMore: React.FC<ReadMoreProps> = ({ text, maxChars, header, textColor, ...typographyProps }) => {
    const [showFullText] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const { themeStyle, device } = useThemeContext();

    const truncatedText = showFullText && text ? text : !!text ? text.slice(0, maxChars) : '';
    const isTruncated = !!text && text.length > maxChars;

    const handleToggleReadMore = () => {
        if (!showFullText && isTruncated) {
            setOpenModal(true);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    return (
        <div>
            <Typography {...typographyProps}>
                <RichTextViewer
                    content={truncatedText}
                    theme={themeStyle}
                    lineClamp={2}
                    enableReadMore
                    hideReadMoreText
                    textStyle={{
                        padding: 0,
                    }}
                    textColor={textColor}
                />
                {isTruncated && (
                    <>
                        {' '}
                        <Link component="button" onClick={handleToggleReadMore}>
                            Read more
                        </Link>
                    </>
                )}
            </Typography>

            <Dialog
                fullScreen={device.isMobile}
                open={openModal}
                onClose={handleCloseModal}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].pure_black,
                        height: {
                            xs: '100%',
                            sm: '50%',
                        },
                        overflowY: 'scroll',
                        border: device.isMobile ? 'none' : '1px solid grey',
                        borderRadius: device.isMobile ? 0 : 3,
                        backdropFilter: 'blur(100px)',
                    },
                }}>
                <CustomLazyLoadImage
                    src={plainCloseIcon}
                    alt="close icon"
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                    }}
                    onClick={handleCloseModal}
                />

                {header && (
                    <Typography
                        sx={{
                            p: 2,
                            color: theme[themeStyle].tag_white,
                            fontSize: 18,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            mb: 1,
                        }}>
                        {header}
                    </Typography>
                )}
                <RichTextViewer content={text} theme={themeStyle} />
            </Dialog>
        </div>
    );
};

export { ReadMore };
